const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface RequestOptions {
  method: string;
  headers?: Record<string, string>;
  body?: any;
  requiresAuth?: boolean;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('idToken');
  }

  private async request<T>(endpoint: string, options: RequestOptions): Promise<T> {
    const { method, headers = {}, body, requiresAuth = false } = options;

    const config: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
    };

    if (requiresAuth) {
      const token = this.getAuthToken();
      if (!token) throw new Error('No authentication token');
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    if (body) config.body = JSON.stringify(body);

    const response = await fetch(`${this.baseURL}${endpoint}`, config);

    // Handle token refresh for 401
    if (response.status === 401 && requiresAuth) {
      const refreshed = await this.attemptTokenRefresh();
      if (refreshed) {
        // Retry original request
        const token = this.getAuthToken();
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
        const retryResponse = await fetch(`${this.baseURL}${endpoint}`, config);
        const data = await retryResponse.json();
        if (!retryResponse.ok) {
          throw new Error(data.message || data.error || 'Request failed');
        }
        return data;
      }
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Request failed');
    }

    return data;
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return false;

      const response = await this.refreshToken(refreshToken) as any;
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('idToken', response.idToken);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Auth
  async login(username: string, password: string) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: { username, password },
    });
  }

  async refreshToken(refreshToken: string) {
    return this.request('/api/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
    });
  }

  // Productos
  async getProductos(params?: { published?: boolean; category?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.set('category', params.category);

    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';

    return this.request(`/api/productos${qs}`, {
      method: 'GET',
      requiresAuth: false,
    });
  }

  async createProducto(data: any) {
    return this.request('/api/productos', {
      method: 'POST',
      body: data,
      requiresAuth: true,
    });
  }

  async updateProducto(productoId: string, data: any) {
    return this.request(`/api/productos/${productoId}`, {
      method: 'PUT',
      body: data,
      requiresAuth: true,
    });
  }

  async deleteProducto(productoId: string) {
    return this.request(`/api/productos/${productoId}`, {
      method: 'DELETE',
      requiresAuth: true,
    });
  }

  async getProductoUploadUrl(fileName: string, fileType: string) {
    return this.request(
      `/api/productos/upload-url?fileName=${encodeURIComponent(fileName)}&fileType=${encodeURIComponent(fileType)}`,
      {
        method: 'GET',
        requiresAuth: true,
      }
    );
  }

  async uploadToS3(presignedUrl: string, file: File) {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload to S3');
    }

    return response;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;
